<!--
  One PPA (/ppa/:id), laid out like the house and sale pages: on a phone one column with the actions in a bar at
  the bottom; from lg, the photos, locations and reviews on the left and a sticky panel on the right (what it is,
  its rating, Write a review). The window scrolls (this route is outside NavComponent).
-->
<div class="min-h-dvh bg-background-light">
  <app-listing-page-header backLink="/ppa" backLabel="All PPAs"></app-listing-page-header>

  <main class="mx-auto max-w-5xl px-4 pt-2 pb-32 lg:pb-12">
    <div *ngIf="isLoading" class="animate-pulse space-y-4" aria-busy="true" aria-label="Loading the PPA">
      <div class="space-y-3 rounded-xl bg-white p-5 shadow-card">
        <div class="h-7 w-2/3 rounded bg-gray-200"></div>
        <div class="h-4 w-1/3 rounded bg-gray-200"></div>
        <div class="h-10 w-1/4 rounded bg-gray-200"></div>
      </div>
      <div class="h-40 rounded-xl bg-white shadow-card"></div>
    </div>

    <div *ngIf="!isLoading && notFound" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">domain</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">We can't find this PPA.</p>
      <p class="m-0 text-sm text-text-secondary">The link may be wrong, or it was removed.</p>
      <a routerLink="/ppa" class="btn-primary mt-2 inline-flex items-center">Find your PPA</a>
    </div>

    <div *ngIf="!isLoading && loadError" class="flex flex-col items-center gap-3 py-20 text-center">
      <mat-icon class="!h-12 !w-12 !text-5xl text-primary/30" aria-hidden="true">wifi_off</mat-icon>
      <p class="m-0 text-base font-medium text-gray-900">Couldn't load this PPA.</p>
      <p class="m-0 text-sm text-text-secondary">Check your connection and try again.</p>
      <button type="button" class="btn-primary mt-2 inline-flex items-center gap-2" (click)="loadPpa()">
        <mat-icon aria-hidden="true">refresh</mat-icon> Try again
      </button>
    </div>

    <div *ngIf="!isLoading && ppa" class="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
      <div class="min-w-0 space-y-4">
        <div *ngIf="ppa.Media?.length" class="relative aspect-[16/10] overflow-hidden rounded-xl bg-primary/5 shadow-card">
          <app-photo-strip [media]="ppa.Media" [alt]="ppa.name" arrows="always" [placeholderIcon]="typeIcon"
            [width]="1000" sizes="(min-width: 1024px) 40rem, 100vw"></app-photo-strip>
        </div>

        <div class="lg:hidden">
          <ng-container *ngTemplateOutlet="summary; context: { $implicit: false }"></ng-container>
        </div>

        <section *ngIf="ppa.Locations?.length" class="rounded-xl bg-white p-5 shadow-card">
          <h2 class="mb-3 text-lg font-semibold text-primary">Where it is</h2>
          <ul class="m-0 list-none space-y-3 p-0">
            <li *ngFor="let location of ppa.Locations" class="flex items-start gap-3">
              <span class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg bg-background-light text-primary" aria-hidden="true">
                <mat-icon class="!size-5 !text-xl !leading-5">pin_drop</mat-icon>
              </span>
              <div class="min-w-0">
                <p class="m-0 font-medium text-gray-900">{{ ppaPlace(location) || 'Location not given' }}</p>
                <p *ngIf="location.address" class="m-0 text-sm text-text-secondary">{{ location.address }}</p>
              </div>
            </li>
          </ul>
          <a *ngIf="housesNearby" routerLink="/search" [queryParams]="housesQueryParams"
            class="mt-4 inline-flex items-center gap-1 font-medium text-primary underline">
            <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">apartment</mat-icon>
            {{ housesNearby }} {{ housesNearby === 1 ? 'house' : 'houses' }} near here
          </a>
        </section>

        <section class="rounded-xl bg-white p-5 shadow-card" aria-labelledby="reviews-heading">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h2 id="reviews-heading" class="m-0 text-lg font-semibold text-primary">
              Reviews <span *ngIf="reviewCount" class="font-normal text-text-secondary">({{ reviewCount | shortenNumber }})</span>
            </h2>
            <button *ngIf="reviews.length" type="button" (click)="writeReview()" [disabled]="postingReview"
              class="inline-flex min-h-10 items-center gap-1 rounded-lg px-3 text-sm font-medium text-primary hover:bg-primary/5 disabled:opacity-50">
              <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">rate_review</mat-icon> Write one
            </button>
          </div>

          <ol *ngIf="reviews.length; else noReviews" class="m-0 list-none divide-y divide-gray-100 p-0">
            <li *ngFor="let review of reviews; trackBy: trackById" [attr.data-review-id]="review.id" tabindex="-1"
              class="py-4 first:pt-0 last:pb-0 scroll-mt-24 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded">
              <div class="flex items-center justify-between gap-3">
                <span class="flex text-[#E5A100]" [attr.aria-label]="(review.star_rating ?? 0) + ' out of 5 stars'">
                  <mat-icon *ngFor="let star of starsFor(review.star_rating)" class="!size-5 !text-xl !leading-5" aria-hidden="true">{{ star }}</mat-icon>
                </span>
                <time [attr.datetime]="review.created_at" class="text-sm text-text-secondary">{{ review.created_at | date: 'd MMM y' }}</time>
              </div>
              <p *ngIf="review.comment?.trim(); else noComment" class="m-0 mt-2 whitespace-pre-line leading-relaxed text-gray-800">{{ review.comment }}</p>
              <ng-template #noComment><p class="m-0 mt-2 text-sm italic text-text-secondary">A rating, no words.</p></ng-template>
              <p class="m-0 mt-2 text-sm text-text-secondary">
                <a *ngIf="reviewerLink(review) as link; else anonymous" [routerLink]="link" class="font-medium text-primary hover:underline">{{ reviewerName(review) }}</a>
                <ng-template #anonymous>{{ reviewerName(review) }}</ng-template>
              </p>
            </li>
          </ol>
          <app-ppa-reviews-more [pager]="reviewPager" [total]="reviewCount" (loaded)="focusReview($event)"></app-ppa-reviews-more>
          <ng-template #noReviews>
            <div class="flex flex-col items-center gap-2 py-6 text-center">
              <mat-icon class="!h-10 !w-10 !text-4xl text-primary/30" aria-hidden="true">rate_review</mat-icon>
              <p class="m-0 font-medium text-gray-900">No reviews yet.</p>
              <p class="m-0 max-w-sm text-sm text-text-secondary">Served here? Say what it's really like - the work, how corpers are treated, what to expect.</p>
              <button type="button" (click)="writeReview()" [disabled]="postingReview" class="btn-primary mt-2 inline-flex items-center gap-2 disabled:opacity-50">
                Be the first to review
              </button>
            </div>
          </ng-template>
        </section>
      </div>

      <aside class="hidden lg:sticky lg:top-24 lg:block">
        <ng-container *ngTemplateOutlet="summary; context: { $implicit: true }"></ng-container>
      </aside>
    </div>
  </main>

  <div *ngIf="!isLoading && ppa"
    class="fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
    <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'bar' }"></ng-container>
  </div>
</div>

<!-- What it is, where, and what corpers make of it - on a wide screen with the actions under it -->
<ng-template #summary let-withActions>
  <section class="rounded-xl bg-white p-5 shadow-card">
    <p class="m-0 mb-2 inline-flex items-center gap-1.5 rounded-lg bg-background-light px-2 py-1 text-sm font-medium text-primary">
      <mat-icon class="!size-4 !text-base !leading-4" aria-hidden="true">{{ typeIcon }}</mat-icon>{{ ppa!.type_of_ppa }}
    </p>
    <h1 class="m-0 text-2xl font-bold leading-tight text-primary">{{ ppa!.name }}</h1>
    <p *ngIf="place" class="m-0 mt-1 text-sm text-text-secondary">{{ place }}</p>

    <div *ngIf="reviewCount; else unrated" class="mt-4 flex items-center gap-4">
      <div class="text-center">
        <p class="m-0 text-4xl font-bold leading-none text-primary">{{ averageRatingDisplay }}</p>
        <span class="mt-1 flex text-[#E5A100]" [attr.aria-label]="averageRatingDisplay + ' out of 5 stars'">
          <mat-icon *ngFor="let star of starsFor(averageRating)" class="!size-4 !text-base !leading-4" aria-hidden="true">{{ star }}</mat-icon>
        </span>
        <p class="m-0 mt-1 text-sm text-text-secondary">{{ reviewCount | shortenNumber }} review{{ reviewCount === 1 ? '' : 's' }}</p>
      </div>
      <!-- How many gave 5 stars, 4 stars, ... (each corp member once, like the average) -->
      <dl *ngIf="hasRatings" class="m-0 flex-1 space-y-1" aria-label="Ratings">
        <div *ngFor="let row of ratingBreakdown" class="flex items-center gap-2 text-sm">
          <dt class="w-3 tabular-nums text-text-secondary">{{ row.stars }}</dt>
          <dd class="m-0 h-2 flex-1 overflow-hidden rounded-full bg-background-light" [attr.aria-label]="row.count + ' with ' + row.stars + ' stars'">
            <span class="block h-full rounded-full bg-[#E5A100]" [style.width.%]="row.percent"></span>
          </dd>
        </div>
      </dl>
    </div>
    <ng-template #unrated>
      <p class="m-0 mt-4 text-sm text-text-secondary">No ratings yet.</p>
    </ng-template>

    <p *ngIf="corperCount" class="m-0 mt-4 flex items-center gap-1.5 text-sm text-gray-800">
      <mat-icon class="!size-5 !text-xl !leading-5 text-primary/70" aria-hidden="true">groups</mat-icon>
      {{ corperCount }} {{ corperCount === 1 ? 'corper' : 'corpers' }} posted here
    </p>

    <div *ngIf="withActions" class="mt-5 flex flex-wrap items-center gap-2">
      <ng-container *ngTemplateOutlet="actions; context: { $implicit: 'panel' }"></ng-container>
    </div>
  </section>
</ng-template>

<!-- Write a review (and Share) - logged out, sign up first. `bar` is the phone's bottom bar, `panel` the summary. -->
<ng-template #actions let-place>
  <ng-container *ngIf="!callerService.isLoggedIn">
    <p *ngIf="place === 'bar'" class="m-0 min-w-0 flex-1 truncate text-sm text-text-secondary">Served here? Join to leave a review.</p>
    <a routerLink="/signup" [queryParams]="signupQueryParams"
      class="btn-primary inline-flex min-h-11 items-center justify-center !py-0" [class.w-full]="place === 'panel'">Sign up free</a>
  </ng-container>

  <ng-container *ngIf="callerService.isLoggedIn">
    <button type="button" (click)="ppaActions.share(ppa!)" aria-label="Share this PPA"
      class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 px-3 font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      [class.flex-1]="place === 'panel'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">share</mat-icon>
      <span *ngIf="place === 'panel'">Share</span>
    </button>
    <button type="button" (click)="writeReview()" [disabled]="postingReview"
      class="btn-primary inline-flex min-h-11 items-center justify-center gap-2 !py-0 disabled:opacity-50"
      [ngClass]="place === 'panel' ? 'order-first w-full' : 'flex-1'">
      <mat-icon class="!size-5 !text-xl !leading-5" aria-hidden="true">rate_review</mat-icon>
      {{ postingReview ? 'Posting your review...' : 'Write a review' }}
    </button>
  </ng-container>
</ng-template>
